import { useEffect, useRef, useState } from "react";
import { BrowserCodeReader, BrowserMultiFormatReader } from "@zxing/browser";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Camera, Keyboard, X, Flashlight, MagnifyingGlassPlus } from "@phosphor-icons/react";

export default function BarcodeScannerDialog({ open, onOpenChange, onDetected }) {
  const [mode, setMode] = useState("camera");
  const [manual, setManual] = useState("");
  const [error, setError] = useState("");
  const [starting, setStarting] = useState(false);
  const [cameraEnabled, setCameraEnabled] = useState(true);

  const [torchAvailable, setTorchAvailable] = useState(false);
  const [torchOn, setTorchOn] = useState(false);

  const [zoomAvailable, setZoomAvailable] = useState(false);
  const [zoomMin, setZoomMin] = useState(1);
  const [zoomMax, setZoomMax] = useState(1);
  const [zoomValue, setZoomValue] = useState(1);

  const videoRef = useRef(null);
  const controlsRef = useRef(null);
  const trackRef = useRef(null);
  const detectedRef = useRef(false);

  const stopCamera = () => {
    try {
      controlsRef.current?.stop?.();
    } catch {}

    controlsRef.current = null;

    const track = trackRef.current;
    trackRef.current = null;

    if (track) {
      try {
        track.stop();
      } catch {}
    }

    const video = videoRef.current;

    if (video?.srcObject) {
      try {
        video.srcObject.getTracks().forEach((t) => t.stop());
      } catch {}

      video.srcObject = null;
    }
  };

  useEffect(() => {
    if (!open || mode !== "camera" || !cameraEnabled) return;

    let disposed = false;

    detectedRef.current = false;
    setError("");
    setStarting(true);
    setTorchAvailable(false);
    setTorchOn(false);
    setZoomAvailable(false);

    const configureTrack = async () => {
      const video = videoRef.current;
      const stream = video?.srcObject;

      if (!stream?.getVideoTracks) return;

      const track = stream.getVideoTracks()[0];
      if (!track) return;

      trackRef.current = track;

      try {
        const capabilities = track.getCapabilities?.() || {};
        const advanced = {};

        if (
          Array.isArray(capabilities.focusMode) &&
          capabilities.focusMode.includes("continuous")
        ) {
          advanced.focusMode = "continuous";
        }

        if (Object.keys(advanced).length) {
          try {
            await track.applyConstraints({
              advanced: [advanced],
            });
          } catch {}
        }

        if ("torch" in capabilities) {
          setTorchAvailable(Boolean(capabilities.torch));
        }

        if (
          capabilities.zoom &&
          typeof capabilities.zoom.min === "number" &&
          typeof capabilities.zoom.max === "number"
        ) {
          const min = capabilities.zoom.min;
          const max = capabilities.zoom.max;

          setZoomMin(min);
          setZoomMax(max);
          setZoomAvailable(max > min);

          const initialZoom = Math.min(
            max,
            Math.max(min, min + (max - min) * 0.15)
          );

          setZoomValue(initialZoom);

          try {
            await track.applyConstraints({
              advanced: [{ zoom: initialZoom }],
            });
          } catch {}
        }
      } catch {
        /*
         * Safari/iOS e alcuni Android non espongono tutte le
         * capabilities. La scansione continua comunque.
         */
      }
    };

    const startCamera = async () => {
      await new Promise((resolve) => window.setTimeout(resolve, 250));
      if (disposed || !videoRef.current) return;
      try {
        const reader = new BrowserMultiFormatReader();

        let controls;

        const onResult = (result) => {
          if (!result || disposed || detectedRef.current) return;

          const value =
            String(result.getText?.() || "").trim();

          if (!value) return;

          detectedRef.current = true;

          stopCamera();

          if (!disposed) {
            onDetected(value);
          }
        };

        controls = await reader.decodeFromConstraints(
          {
            audio: false,
            video: {
              facingMode: { ideal: "environment" },
              width: { ideal: 1280 },
              height: { ideal: 720 },
            },
          },
          videoRef.current,
          onResult
        );

        if (disposed) {
          try {
            controls.stop();
          } catch {}
          return;
        }

        controlsRef.current = controls;

        const video = videoRef.current;
        if (video) {
          video.muted = true;
          video.setAttribute("playsinline", "true");
          try {
            await video.play();
          } catch {}
        }
        setStarting(false);

        window.setTimeout(() => {
          if (!disposed) {
            void configureTrack();
          }
        }, 500);
      } catch (e) {
        stopCamera();

        if (disposed) return;

        setStarting(false);

        const name = e?.name || "";

        if (
          name === "NotAllowedError" ||
          name === "PermissionDeniedError"
        ) {
          setError(
            "Fotocamera non autorizzata. Apri le impostazioni dell'app o del browser, abilita il permesso Fotocamera e poi riapri CantierePro."
          );
        } else if (
          name === "NotFoundError" ||
          name === "DevicesNotFoundError"
        ) {
          setError(
            "Nessuna fotocamera disponibile sul dispositivo."
          );
        } else if (
          name === "NotReadableError" ||
          name === "TrackStartError"
        ) {
          setError(
            "La fotocamera è già utilizzata da un'altra applicazione. Chiudila e riprova."
          );
        } else {
          setError(
            "Impossibile avviare la fotocamera. Riprova oppure usa l'inserimento manuale."
          );
        }
      }
    };

    void startCamera();

    return () => {
      disposed = true;
      stopCamera();
    };
  }, [open, mode, cameraEnabled, onDetected]);

  useEffect(() => {
    if (open) {
      detectedRef.current = false;
      setError("");
      setMode("camera");
      setCameraEnabled(true);
      setTorchOn(false);
    } else {
      detectedRef.current = false;
      setError("");
      setMode("camera");
      setCameraEnabled(false);
      setTorchOn(false);
    }
  }, [open]);

  const toggleTorch = async () => {
    const track = trackRef.current;
    if (!track) return;

    const next = !torchOn;

    try {
      await track.applyConstraints({
        advanced: [{ torch: next }],
      });

      setTorchOn(next);
    } catch {
      setError(
        "La torcia non può essere controllata su questo dispositivo."
      );
    }
  };

  const changeZoom = async (value) => {
    const numeric = Number(value);
    const track = trackRef.current;

    setZoomValue(numeric);

    if (!track) return;

    try {
      await track.applyConstraints({
        advanced: [{ zoom: numeric }],
      });
    } catch {}
  };

  const submitManual = (e) => {
    e?.preventDefault?.();

    const value = manual.trim();

    if (value) {
      onDetected(value);
      setManual("");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-md bg-white"
        data-testid="barcode-scanner-dialog"
      >
        <DialogHeader>
          <DialogTitle className="font-display tracking-tight">
            Scansiona barcode
          </DialogTitle>
        </DialogHeader>

        <div className="mb-2 grid grid-cols-2 gap-2">
          <button
            type="button"
            onClick={() => { setError(""); setCameraEnabled(true); setMode("camera"); }}
            className={`rounded-md border py-2 text-sm ${
              mode === "camera"
                ? "border-slate-900 bg-slate-900 text-white"
                : "border-slate-200 bg-white text-slate-700"
            }`}
          >
            <Camera size={14} weight="bold" className="mr-1.5 inline" />
            Fotocamera
          </button>

          <button
            type="button"
            onClick={() => {
              setCameraEnabled(false);
              setMode("manual");
            }}
            className={`rounded-md border py-2 text-sm ${
              mode === "manual"
                ? "border-slate-900 bg-slate-900 text-white"
                : "border-slate-200 bg-white text-slate-700"
            }`}
          >
            <Keyboard size={14} weight="bold" className="mr-1.5 inline" />
            Inserimento
          </button>
        </div>

        {mode === "camera" ? (
          <div>
            {!cameraEnabled ? (
              <div className="flex aspect-[4/3] w-full flex-col items-center justify-center rounded-lg bg-slate-950 px-6 text-center">
                <Camera size={46} weight="duotone" className="mb-4 text-white" />

                <div className="mb-2 text-base font-semibold text-white">
                  Fotocamera necessaria
                </div>

                <div className="mb-5 text-xs leading-5 text-slate-300">
                  Tocca il pulsante per consentire a CantierePro di usare la fotocamera e leggere il barcode.
                </div>

                <Button
                  type="button"
                  onClick={() => {
                    setError("");
                    setCameraEnabled(true);
                  }}
                  className="bg-white text-slate-900 hover:bg-slate-100"
                  data-testid="scanner-enable-camera"
                >
                  <Camera size={17} weight="bold" className="mr-2" />
                  Attiva fotocamera
                </Button>
              </div>
            ) : (
              <>
                <div className="relative aspect-[4/3] w-full overflow-hidden rounded-lg bg-slate-950">
                  <video
                ref={videoRef}
                autoPlay
                muted
                playsInline
                className="h-full w-full object-cover"
              />

              <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
                <div className="h-[30%] w-[86%] rounded-lg border-2 border-white shadow-[0_0_0_999px_rgba(0,0,0,0.30)]" />
              </div>

              {starting && (
                <div className="absolute inset-0 flex items-center justify-center bg-slate-950/60 text-sm font-medium text-white">
                  Avvio fotocamera...
                </div>
              )}
            </div>

            {(torchAvailable || zoomAvailable) && (
              <div className="mt-3 rounded-lg border border-slate-200 bg-slate-50 p-3">
                {torchAvailable && (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={toggleTorch}
                    className="mb-3 w-full bg-white"
                  >
                    <Flashlight size={17} weight="bold" className="mr-2" />
                    {torchOn ? "Spegni torcia" : "Accendi torcia"}
                  </Button>
                )}

                {zoomAvailable && (
                  <div>
                    <div className="mb-1 flex items-center justify-between text-xs text-slate-600">
                      <span className="inline-flex items-center gap-1">
                        <MagnifyingGlassPlus size={14} />
                        Zoom
                      </span>
                      <span>{zoomValue.toFixed(1)}×</span>
                    </div>

                    <input
                      type="range"
                      min={zoomMin}
                      max={zoomMax}
                      step="0.1"
                      value={zoomValue}
                      onChange={(e) => changeZoom(e.target.value)}
                      className="w-full"
                    />
                  </div>
                )}
              </div>
            )}

            {cameraEnabled && error && (
              <div className="mt-2 text-xs text-red-600">
                {error}
              </div>
            )}

                <div className="mt-2 text-xs text-slate-500">
              Inquadra il barcode orizzontalmente nel riquadro.
              Avvicina lentamente il telefono finché il codice è nitido.
                </div>
              </>
            )}
          </div>
        ) : (
          <form onSubmit={submitManual} className="space-y-3">
            <Input
              autoFocus
              placeholder="Digita o incolla il codice…"
              value={manual}
              onChange={(e) => setManual(e.target.value)}
              className="h-11"
            />

            <Button
              type="submit"
              className="h-11 w-full bg-slate-900 text-white hover:bg-slate-800"
            >
              Cerca prodotto
            </Button>
          </form>
        )}

        <button
          type="button"
          onClick={() => onOpenChange(false)}
          className="mt-2 inline-flex items-center gap-1 text-xs text-slate-500 hover:text-slate-900"
        >
          <X size={12} />
          Chiudi
        </button>
      </DialogContent>
    </Dialog>
  );
}
